import axios from 'axios'

// Always use reverse-proxied relative API path from Nginx.
const API_BASE_URL = '/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 70000,
  withCredentials: true,  // Enable sending/receiving cookies with cross-origin requests
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
// Auth tokens are in httpOnly cookies, sent automatically by the browser
api.interceptors.request.use(
  (config) => {
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // If user is already logged out or on login page, don't try to refresh
    const isLoggedOut = !localStorage.getItem('username')
    const isOnLoginPage = window.location.pathname.includes('/login')
    
    if (isLoggedOut || isOnLoginPage) {
      // User already logged out, just reject the error without any action
      return Promise.reject(error)
    }
    
    // Don't retry if this is already a retry, or if it's the refresh/logout endpoint
    if (error.response?.status === 401 && 
        !originalRequest._retry && 
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/logout')) {
      originalRequest._retry = true
      
      try {
        // Try to refresh the token first
        await api.post('/auth/refresh')
        console.log('✅ Token refreshed via interceptor')
        // Retry the original request
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed - session truly expired
        console.log('❌ Session expired, redirecting to login')
        
        // Clear localStorage
        localStorage.removeItem('username')
        localStorage.removeItem('mail')
        
        // Redirect to login
        window.location.href = '/login'
        
        return Promise.reject(refreshError)
      }
    }
    
    // If this is the refresh or logout endpoint failing, just clear and redirect once
    if (error.response?.status === 401 && 
        (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/logout'))) {
      console.log('❌ Auth endpoint failed')
      // Clear localStorage
      localStorage.removeItem('username')
      localStorage.removeItem('mail')
      // Redirect to login only if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    
    return Promise.reject(error)
  }
)

// Certificate API endpoints
export const certificateApi = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.awsAccount) params.append('aws_account', filters.awsAccount)
    if (filters.status) params.append('status', filters.status)
    if (filters.productName) params.append('product_name', filters.productName)
    if (filters.skip !== undefined) params.append('skip', filters.skip)
    if (filters.limit !== undefined) params.append('limit', filters.limit)
    return api.get(`/certificates?${params.toString()}`)
  },
  
  getAWS: (filters = {}) => {
    return certificateApi.getAll({ ...filters, exclude_onprem: true })
  },
  
  getOnPrem: (filters = {}) => {
    return certificateApi.getAll({ ...filters, aws_account: 'ONPREM' })
  },
  
  getById: (id) => api.get(`/certificates/${id}`),
  create: (data) => api.post('/certificates', data),
  onboard: (data) => api.post('/certificates/onboard', data, { timeout: 300000 }), // Venafi integration endpoint (long-running: folder creation + cert issuance polling)
  bulkOnboard: (dataArray) => api.post('/certificates/bulk-onboard', { certificates: dataArray }, { timeout: 300000 }), // Bulk pre-existing cert onboarding
  checkOnboarded: (commonName) => {
    const params = new URLSearchParams()
    params.append('common_name', commonName)
    return api.get(`/certificates/check-onboarded?${params.toString()}`)
  },
  checkVenafiExists: (commonName, maxResults = 25) => {
    const params = new URLSearchParams()
    params.append('common_name', commonName)
    params.append('max_results', maxResults)
    return api.get(`/certificates/venafi/exists?${params.toString()}`)
  },
  getItsmGroups: () => api.get('/certificates/itsm-groups'),
  getVenafiCertificateDetails: (certificateDn, guid = null) => {
    const params = new URLSearchParams()
    params.append('certificate_dn', certificateDn)
    if (guid) params.append('guid', guid)
    return api.get(`/certificates/venafi/certificate-details?${params.toString()}`)
  },
  update: (id, data) => api.put(`/certificates/${id}`, data),
  delete: (id) => api.delete(`/certificates/${id}`),
}

// Product/Account API endpoints
export const productApi = {
  getAccounts: () => api.get('/accounts'),
  getProducts: () => api.get('/products'),
  onboard: (data) => api.post('/products/onboard', data),
  getDL: (productName) => api.get(`/products/${productName}/dl`),
  updateDL: (productName, dlData) => api.put(`/products/${productName}/dl`, dlData),
}

// ITSM Products API endpoints
export const itsmProductApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params.search)
    if (params.skip !== undefined) queryParams.append('skip', params.skip)
    if (params.limit !== undefined) queryParams.append('limit', params.limit)
    return api.get(`/itsm-products?${queryParams.toString()}`)
  },
  getById: (id) => api.get(`/itsm-products/${id}`),
  create: (data) => api.post('/itsm-products', data),
  update: (id, data) => api.put(`/itsm-products/${id}`, data),
  delete: (id) => api.delete(`/itsm-products/${id}`),
}

// Product Distribution Lists API endpoints
export const productDlApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.product_code) queryParams.append('product_code', params.product_code)
    if (params.skip !== undefined) queryParams.append('skip', params.skip)
    if (params.limit !== undefined) queryParams.append('limit', params.limit)
    return api.get(`/product-dls?${queryParams.toString()}`)
  },
  getById: (id) => api.get(`/product-dls/${id}`),
  create: (data) => api.post('/product-dls', data),
  update: (id, data) => api.put(`/product-dls/${id}`, data),
  delete: (id) => api.delete(`/product-dls/${id}`),
}

// AWS CSG API endpoints
export const awsCsgApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.product_code) queryParams.append('product_code', params.product_code)
    if (params.search) queryParams.append('search', params.search)
    if (params.skip !== undefined) queryParams.append('skip', params.skip)
    if (params.limit !== undefined) queryParams.append('limit', params.limit)
    return api.get(`/aws-csg?${queryParams.toString()}`)
  },
  getByProduct: (productCode) => api.get(`/aws-csg/by-product/${productCode}`),
  getByAccountNumber: (accountNumber) => api.get(`/aws-csg/${accountNumber}`),
  create: (data) => api.post('/aws-csg', data),
  update: (accountNumber, data) => api.put(`/aws-csg/${accountNumber}`, data),
  delete: (accountNumber) => api.delete(`/aws-csg/${accountNumber}`),
}

// AWS Certificate Accounts API endpoints
export const awsCertAccountApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.product_env) queryParams.append('product_env', params.product_env)
    if (params.aws_account_number) queryParams.append('aws_account_number', params.aws_account_number)
    if (params.skip !== undefined) queryParams.append('skip', params.skip)
    if (params.limit !== undefined) queryParams.append('limit', params.limit)
    return api.get(`/awscert-accounts?${queryParams.toString()}`)
  },
  getOnboardedProducts: (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params.search)
    if (params.limit !== undefined) queryParams.append('limit', params.limit)
    return api.get(`/awscert-accounts/onboarded-products?${queryParams.toString()}`)
  },
  getOnboardedAccountsByProduct: (productCode) => api.get(`/awscert-accounts/onboarded-accounts/${productCode}`),
  getAvailableAccountsByProduct: (productCode) => api.get(`/awscert-accounts/available-accounts/${productCode}`),
  getByKey: (productEnv, accountNumber) => api.get(`/awscert-accounts/${productEnv}/${accountNumber}`),
  create: (data) => api.post('/awscert-accounts', data),
  update: (productEnv, accountNumber, data) => api.put(`/awscert-accounts/${productEnv}/${accountNumber}`, data),
  delete: (productEnv, accountNumber) => api.delete(`/awscert-accounts/${productEnv}/${accountNumber}`),
}

// CRQ Deployments API endpoints
export const crqDeploymentApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.product_code) queryParams.append('product_code', params.product_code)
    if (params.environment) queryParams.append('environment', params.environment)
    if (params.page !== undefined) queryParams.append('page', params.page)
    if (params.page_size !== undefined) queryParams.append('page_size', params.page_size)
    return api.get(`/crq-deployments?${queryParams.toString()}`)
  },
  getById: (crqId) => api.get(`/crq-deployments/${crqId}`),
}

// Distribution API endpoints
export const distributionApi = {
  getAll: (filters = {}) => api.get('/distributions', { params: filters }),
  getByCertificate: (certId) => api.get(`/distributions?certificate_id=${certId}`),
  create: (data) => api.post('/distributions', data),
  update: (id, data) => api.put(`/distributions/${id}`, data),
  delete: (id) => api.delete(`/distributions/${id}`),
}

export default api

